import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { Copy, Trash2, Ungroup, BookOpen } from 'lucide-react';

interface CanvasContextMenuProps {
  x: number;
  y: number;
  hasSelection: boolean;
  hasClipboard: boolean;
  hasGroupSelected: boolean;
  onClose: () => void;
  onCopy: () => void;
  onPaste: () => void;
  onDelete: () => void;
  onUngroup?: () => void;
  onOpenWorkflows?: () => void;
}

const CanvasContextMenu: React.FC<CanvasContextMenuProps> = ({
  x,
  y,
  hasSelection,
  hasClipboard,
  hasGroupSelected,
  onClose,
  onCopy,
  onPaste,
  onDelete,
  onUngroup,
  onOpenWorkflows,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);
  return (
    <div
      ref={menuRef}
      className="fixed z-[100] bg-slate-800 border border-slate-600 rounded-lg shadow-2xl py-1 min-w-[180px] animate-in fade-in zoom-in-95 duration-100"
      style={{ left: x, top: y }}
    >
      {/* Workflows Option */}
      {onOpenWorkflows && (
        <>
          <button
            onClick={() => {
              onOpenWorkflows();
              onClose();
            }}
            className="w-full px-3 py-2 text-left text-sm text-blue-400 hover:bg-slate-700 flex items-center gap-2 transition-colors cursor-pointer"
          >
            <BookOpen size={14} />
            Workflows
          </button>
          <div className="h-px bg-slate-700 my-1" />
        </>
      )}      {/* Copy Option */}
      <button
        onClick={() => {
          if (hasSelection) {
            onCopy();
            onClose();
          }
        }}
        disabled={!hasSelection}
        className={clsx(
          "w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition-colors",
          hasSelection
            ? "text-slate-200 hover:bg-slate-700 cursor-pointer"
            : "text-slate-500 cursor-not-allowed"
        )}
      >
        <Copy size={14} />
        Copy (Ctrl+C)
      </button>

      {/* Paste Option */}
      <button
        onClick={() => {
          if (hasClipboard) {
            onPaste();
            onClose();
          }
        }}
        disabled={!hasClipboard}
        className={clsx(
          "w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition-colors",
          hasClipboard
            ? "text-slate-200 hover:bg-slate-700 cursor-pointer"
            : "text-slate-500 cursor-not-allowed"
        )}
      >
        <Copy size={14} />
        Paste (Ctrl+V)
      </button>

      {/* Delete Option */}
      {hasSelection && (
        <>
          <div className="h-px bg-slate-700 my-1" />
          <button
            onClick={() => {
              onDelete();
              onClose();
            }}
            className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-slate-700 flex items-center gap-2 transition-colors cursor-pointer"
          >
            <Trash2 size={14} />
            Delete (Del)
          </button>
        </>
      )}

      {/* Ungroup Option */}
      {hasGroupSelected && onUngroup && (
        <>
          <div className="h-px bg-slate-700 my-1" />
          <button
            onClick={() => {
              onUngroup();
              onClose();
            }}
            className="w-full px-3 py-2 text-left text-sm text-blue-400 hover:bg-slate-700 flex items-center gap-2 transition-colors cursor-pointer"
          >
            <Ungroup size={14} />
            Ungroup
          </button>
        </>
      )}
    </div>
  );
};

export default CanvasContextMenu;
